import { Kbd } from "@components/Kbd";
import {
  goToTokensFor,
  searchShortcutTokens,
  SWITCH_USER_TOKENS,
} from "@components/keyboardShortcuts";
import { useUser } from "@context/UserContext";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faChevronDown,
  faGear,
  faKeyboard,
  faMagnifyingGlass,
  faUser,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn, Popover, useListboxNav } from "@scribenote/brian-design-system";
import { useNavigate } from "@tanstack/react-router";
import { canAccessAdmin } from "@users/userClient";
import { useEffect, useId, useRef, useState } from "react";

const initials = (name: string) =>
  name
    .replace(/^Dr\.\s*/, "")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const Avatar = ({ name, onDesk }: { name: string; onDesk: boolean }) => (
  <span
    className={cn(
      "flex aspect-square w-8 shrink-0 items-center justify-center rounded-full text-sm font-medium text-ink",
      onDesk ? "bg-paper" : "bg-recess",
    )}
    aria-hidden="true"
  >
    {initials(name)}
  </span>
);

interface MenuAction {
  id: string;
  label: string;
  icon: IconDefinition;
  tokens: string[];
  testId: string;
  run: () => void;
}

/**
 * User control at the end of the top-right control cluster. Shows the signed-in
 * user; the menu lets them switch user (drops back to the access-code screen,
 * where log out now lives), open search, view shortcuts, or jump to Admin.
 *
 * `onDarkBackdrop` is true in the workspace, where the cluster sits directly on
 * the desk and writes in `text-ink-inverse`; the header band still hands us its
 * own paper surface.
 */
export const UserMenu = ({
  onDarkBackdrop = false,
  onOpenSearch,
  onOpenShortcuts,
}: {
  onDarkBackdrop?: boolean;
  onOpenSearch: () => void;
  onOpenShortcuts: () => void;
}) => {
  const { currentUser, switchUser } = useUser();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const itemId = (index: number) => `${menuId}-item-${index}`;

  const actions: MenuAction[] = [
    {
      id: "switch-user",
      label: "Switch user",
      icon: faUser,
      tokens: SWITCH_USER_TOKENS,
      testId: "header-switch-user",
      run: switchUser,
    },
    {
      id: "search",
      label: "Search",
      icon: faMagnifyingGlass,
      tokens: searchShortcutTokens(),
      testId: "header-search",
      run: onOpenSearch,
    },
    {
      id: "shortcuts",
      label: "Keyboard shortcuts",
      icon: faKeyboard,
      tokens: ["?"],
      testId: "header-keyboard-shortcuts",
      run: onOpenShortcuts,
    },
  ];
  if (canAccessAdmin(currentUser)) {
    actions.push({
      id: "admin",
      label: "Admin",
      icon: faGear,
      tokens: goToTokensFor("admin"),
      testId: "header-admin",
      run: () => navigate({ to: "/admin" }),
    });
  }

  const nav = useListboxNav({
    itemCount: actions.length,
    getLabel: (index) => actions[index]?.label ?? "",
    onSelect: (index) => runAt(index),
    onDismiss: () => closeMenu(),
    /* No row is a real focus target, so a Tab would otherwise leave the menu
       open in the top layer with nothing focused to drive or dismiss it. */
    onTabOut: () => closeMenu(),
  });

  /** Close and hand focus back, for closes the user drove from inside the menu. */
  const closeMenu = () => {
    setOpen(false);
    nav.setActiveIndex(-1);
    triggerRef.current?.focus();
  };

  const runAt = (index: number) => {
    const action = actions[index];
    if (!action) return;
    closeMenu();
    action.run();
  };

  const openMenu = (edge: "first" | "last") => {
    setOpen(true);
    nav.setActiveIndex(edge === "first" ? 0 : actions.length - 1);
  };

  /*
   * Light dismiss fires on the same press that reaches the trigger's click, so by
   * click time React already thinks the menu is closed and a naive toggle would
   * reopen it. The press itself is the honest source of truth, so record the open
   * state there. Cleared on keydown, where there was no press to read.
   */
  const openAtPressRef = useRef<boolean | null>(null);

  const handleTriggerClick = () => {
    const wasOpen = openAtPressRef.current ?? open;
    openAtPressRef.current = null;
    if (wasOpen) {
      setOpen(false);
      nav.setActiveIndex(-1);
      return;
    }
    openMenu("first");
  };

  const handleTriggerKeyDown: React.KeyboardEventHandler = (event) => {
    openAtPressRef.current = null;
    if (open) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      openMenu("first");
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      openMenu("last");
    }
  };

  /* The menu owns focus while open; the platform shows the popover in the child's
     effect, which runs before this one, so the element is focusable by now. */
  useEffect(() => {
    if (open) menuRef.current?.focus();
  }, [open]);

  const renderAction = (action: MenuAction, index: number) => {
    const active = index === nav.activeIndex;
    return (
      <div
        key={action.id}
        id={itemId(index)}
        role="menuitem"
        // `id` rather than `label`: both are literals today, but an id is a code
        // identifier by contract where a label is display text that could later
        // interpolate something.
        data-dd-action-name={`User menu: ${action.id}`}
        data-testid={action.testId}
        onPointerEnter={() => nav.setActiveIndex(index)}
        onClick={() => runAt(index)}
        className={cn(
          "flex cursor-default items-center gap-2.5 rounded-paper px-3 py-2 text-sm select-none",
          "transition-colors duration-150 ease-paper motion-reduce:transition-none",
          active ? "bg-recess text-ink" : "text-ink-body",
        )}
      >
        <FontAwesomeIcon
          icon={action.icon}
          aria-hidden="true"
          className="w-4 shrink-0 text-annotation"
        />
        <span className="min-w-0 truncate">{action.label}</span>
        <Kbd tokens={action.tokens} className="ml-auto" />
      </div>
    );
  };

  const [switchUserAction, ...viewActions] = actions;

  return (
    // The name is the only elastic thing in a fixed-width control cluster, so
    // this is the item that gives: `min-w-0` lets it shrink below its text's
    // intrinsic width and the name/role truncate instead of forcing the sibling
    // icon buttons to squash.
    <div className="min-w-0">
      <button
        type="button"
        ref={triggerRef}
        onPointerDown={() => {
          openAtPressRef.current = open;
        }}
        onClick={handleTriggerClick}
        onKeyDown={handleTriggerKeyDown}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        // The trigger renders the signed-in user's name and role, so without a
        // fixed name here the action would be named after them.
        data-dd-action-name="User menu"
        data-testid="header-user-menu"
        className={cn(
          "flex w-full cursor-pointer items-center gap-2.5 rounded-full px-2 py-1 text-left font-body",
          "transition-colors duration-150 ease-paper motion-reduce:transition-none",
          "focus-visible:outline-2 focus-visible:outline-offset-2",
          onDarkBackdrop
            ? "text-ink-inverse hover:bg-paper/10 focus-visible:outline-accent-lite"
            : "text-ink hover:bg-recess focus-visible:outline-accent",
        )}
      >
        <Avatar
          name={currentUser?.displayName ?? "?"}
          onDesk={onDarkBackdrop}
        />
        <span className="min-w-0">
          <span
            data-testid="header-user-name"
            className="block truncate text-sm font-medium"
          >
            {currentUser?.displayName ?? "Account"}
          </span>
          {currentUser && (
            <span
              className={cn(
                "block truncate text-xs",
                onDarkBackdrop ? "text-ink-inverse/75" : "text-annotation",
              )}
            >
              {currentUser.role}
            </span>
          )}
        </span>
        <FontAwesomeIcon
          icon={faChevronDown}
          aria-hidden="true"
          className={cn(
            "ml-1 shrink-0 text-xs transition-transform duration-150 ease-paper motion-reduce:transition-none",
            onDarkBackdrop ? "text-ink-inverse/75" : "text-annotation",
            open && "rotate-180",
          )}
        />
      </button>

      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) nav.setActiveIndex(-1);
        }}
        anchorRef={triggerRef}
        placement="bottom-end"
        id={menuId}
        role="menu"
        ref={menuRef}
        tabIndex={-1}
        aria-activedescendant={
          nav.activeIndex >= 0 ? itemId(nav.activeIndex) : undefined
        }
        onKeyDown={nav.handleKeyDown}
        /* The highlighted row is the focus indicator for the whole composite. */
        className="w-[17rem] focus-visible:outline-none"
      >
        {renderAction(switchUserAction, 0)}

        <div role="separator" className="my-1 border-t border-rule" />

        {viewActions.map((action, index) => renderAction(action, index + 1))}
      </Popover>
    </div>
  );
};
